import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type {
  BookingExpenseInvoiceCompanyLookup,
} from '../Service/bookingExpenseInvoiceCompaniesLookupMapper';
import type {
  BookingExpenseInvoiceTagLookup,
} from '../Service/bookingExpenseInvoiceTagsLookupMapper';
import type {
  CreateBookingSimplifiedCostRequest,
  CurrencyOption,
  SupportedCurrency,
} from '../Types/bookingSimplifiedCost.types';
import { currencyOptions } from '../Types/bookingSimplifiedCost.types';
import type { BookingSimplifiedCostErrorCategory } from '../Service/bookingSimplifiedCostErrorClassifier';

interface BookingSimplifiedCostModalProps {
  open: boolean;
  isSubmitting: boolean;
  initialCompanyId?: number | null;
  isCompanySelectionLocked?: boolean;
  companies: BookingExpenseInvoiceCompanyLookup[];
  tags: BookingExpenseInvoiceTagLookup[];
  companiesLoading: boolean;
  tagsLoading: boolean;
  submitErrorMessage: string | null;
  submitErrorCategory: BookingSimplifiedCostErrorCategory | null;
  canManualRetry: boolean;
  onClose: () => void;
  onSubmit: (request: CreateBookingSimplifiedCostRequest) => void | Promise<void>;
  onManualRetry: () => void | Promise<void>;
  onFallbackToExpenseInvoices: () => void;
}

interface BookingSimplifiedCostFormState {
  companyId: number | null;
  issueDate: string;
  totalGross: string;
  currency: SupportedCurrency;
  description: string;
  assignmentTagId: number | null;
  tagIds: number[];
}

interface BookingSimplifiedCostFormErrors {
  companyId?: string;
  issueDate?: string;
  totalGross?: string;
  currency?: string;
  description?: string;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_CURRENCY: SupportedCurrency = 'PLN';

dayjs.extend(customParseFormat);

const resolveInitialCompanyId = (companyId: number | null | undefined): number | null =>
  typeof companyId === 'number' && Number.isInteger(companyId) && companyId > 0 ? companyId : null;

const buildInitialState = (initialCompanyId: number | null | undefined): BookingSimplifiedCostFormState => ({
  companyId: resolveInitialCompanyId(initialCompanyId),
  issueDate: dayjs().format(DATE_FORMAT),
  totalGross: '',
  currency: DEFAULT_CURRENCY,
  description: '',
  assignmentTagId: null,
  tagIds: [],
});

const validateForm = (state: BookingSimplifiedCostFormState): BookingSimplifiedCostFormErrors => {
  const errors: BookingSimplifiedCostFormErrors = {};

  if (!Number.isInteger(state.companyId) || (state.companyId ?? 0) <= 0) {
    errors.companyId = 'Firma jest wymagana.';
  }

  const parsedDate = dayjs(state.issueDate, DATE_FORMAT, true);
  if (!parsedDate.isValid()) {
    errors.issueDate = 'Data dokumentu jest wymagana i musi mieć format YYYY-MM-DD.';
  } else if (parsedDate.isAfter(dayjs(), 'day')) {
    errors.issueDate = 'Data dokumentu nie może być w przyszłości.';
  }

  const parsedTotalGross = Number(state.totalGross);
  if (!Number.isFinite(parsedTotalGross) || parsedTotalGross === 0) {
    errors.totalGross = 'Kwota brutto musi być różna od zera.';
  }

  if (!currencyOptions.some((o) => o.code === state.currency)) {
    errors.currency = 'Nieprawidłowa waluta.';
  }

  const normalizedDescription = state.description.trim();
  if (!normalizedDescription) {
    errors.description = 'Opis kosztu jest wymagany.';
  } else if (normalizedDescription.length > 1000) {
    errors.description = 'Opis nie może przekraczać 1000 znaków.';
  }

  return errors;
};

const BookingSimplifiedCostModal: React.FC<BookingSimplifiedCostModalProps> = ({
  open,
  isSubmitting,
  initialCompanyId = null,
  isCompanySelectionLocked = false,
  companies,
  tags,
  companiesLoading,
  tagsLoading,
  submitErrorMessage,
  submitErrorCategory,
  canManualRetry,
  onClose,
  onSubmit,
  onManualRetry,
  onFallbackToExpenseInvoices,
}) => {
  const [formState, setFormState] = useState<BookingSimplifiedCostFormState>(() =>
    buildInitialState(initialCompanyId)
  );
  const [formErrors, setFormErrors] = useState<BookingSimplifiedCostFormErrors>({});

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormState(buildInitialState(initialCompanyId));
    setFormErrors({});
  }, [initialCompanyId, open]);

  useEffect(() => {
    if (!open || isCompanySelectionLocked !== true) {
      return;
    }

    const resolvedInitialCompanyId = resolveInitialCompanyId(initialCompanyId);
    if (!resolvedInitialCompanyId) {
      return;
    }

    setFormState((previousState) =>
      previousState.companyId === resolvedInitialCompanyId
        ? previousState
        : {
            ...previousState,
            companyId: resolvedInitialCompanyId,
          }
    );
    setFormErrors((previousErrors) => ({ ...previousErrors, companyId: undefined }));
  }, [initialCompanyId, isCompanySelectionLocked, open]);

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === formState.companyId) ?? null,
    [companies, formState.companyId]
  );

  const selectedTags = useMemo(
    () => tags.filter((tag) => formState.tagIds.includes(tag.id)),
    [formState.tagIds, tags]
  );
  const selectedAssignmentTag = useMemo(
    () => tags.find((tag) => tag.id === formState.assignmentTagId) ?? null,
    [formState.assignmentTagId, tags]
  );

  const isLookupLoading = companiesLoading || tagsLoading;
  const isSubmitDisabled = isSubmitting || isLookupLoading;
  const isCompanyAutocompleteDisabled = isSubmitting || isCompanySelectionLocked;
  const companyHelperText =
    formErrors.companyId ||
    (isCompanySelectionLocked ? 'Firma została ustawiona automatycznie na podstawie oferty bookingu.' : undefined);

  const handleSubmit = async () => {
    const validationErrors = validateForm(formState);
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const deduplicatedTagIds = Array.from(new Set(formState.tagIds));

    await onSubmit({
      companyId: formState.companyId as number,
      issueDate: formState.issueDate,
      totalGross: Number(formState.totalGross),
      currency: formState.currency,
      description: formState.description.trim(),
      ...(formState.assignmentTagId !== null ? { assignmentTagId: formState.assignmentTagId } : {}),
      ...(deduplicatedTagIds.length > 0 ? { tagIds: deduplicatedTagIds } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Dodaj koszt uproszczony</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Alert severity="info">
            Dokument zostanie automatycznie przypisany do bieżącego bookingu z alokacją 100% kwoty
            brutto i będzie widoczny także na liście expense-invoices.
          </Alert>

          {submitErrorMessage && (
            <Alert
              severity="error"
              action={
                <Stack direction="row" spacing={1}>
                  {submitErrorCategory === 'endpointUnavailable' && (
                    <Button color="inherit" size="small" onClick={onFallbackToExpenseInvoices}>
                      Otwórz expense-invoices
                    </Button>
                  )}
                  {canManualRetry && (
                    <Button color="inherit" size="small" onClick={onManualRetry} disabled={isSubmitting}>
                      Ponów
                    </Button>
                  )}
                </Stack>
              }
            >
              {submitErrorMessage}
            </Alert>
          )}

          <Autocomplete
            options={companies}
            loading={companiesLoading}
            disabled={isCompanyAutocompleteDisabled}
            value={selectedCompany}
            onChange={(_, value) => {
              if (isCompanySelectionLocked) {
                return;
              }

              setFormState((previousState) => ({
                ...previousState,
                companyId: value?.id ?? null,
              }));
              setFormErrors((previousErrors) => ({ ...previousErrors, companyId: undefined }));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Firma"
                required
                error={Boolean(formErrors.companyId)}
                helperText={companyHelperText}
              />
            )}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Data dokumentu"
              type="date"
              value={formState.issueDate}
              onChange={(event) => {
                setFormState((previousState) => ({
                  ...previousState,
                  issueDate: event.target.value,
                }));
                setFormErrors((previousErrors) => ({ ...previousErrors, issueDate: undefined }));
              }}
              InputLabelProps={{ shrink: true }}
              error={Boolean(formErrors.issueDate)}
              helperText={formErrors.issueDate}
            />

            <TextField
              label="Kwota brutto"
              type="number"
              value={formState.totalGross}
              onChange={(event) => {
                setFormState((previousState) => ({
                  ...previousState,
                  totalGross: event.target.value,
                }));
                setFormErrors((previousErrors) => ({ ...previousErrors, totalGross: undefined }));
              }}
              inputProps={{ step: 0.01 }}
              error={Boolean(formErrors.totalGross)}
              helperText={formErrors.totalGross}
            />

            <Autocomplete<CurrencyOption, false, true>
              options={currencyOptions}
              value={currencyOptions.find((o) => o.code === formState.currency) ?? currencyOptions[0]}
              onChange={(_, value) => {
                setFormState((previousState) => ({
                  ...previousState,
                  currency: value.code,
                }));
                setFormErrors((previousErrors) => ({ ...previousErrors, currency: undefined }));
              }}
              getOptionLabel={(option) => `${option.code} — ${option.label}`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Waluta"
                  error={Boolean(formErrors.currency)}
                  helperText={formErrors.currency}
                />
              )}
            />
          </Box>

          <Autocomplete
            options={tags}
            loading={tagsLoading}
            value={selectedAssignmentTag}
            onChange={(_, value) => {
              setFormState((previousState) => ({
                ...previousState,
                assignmentTagId: value?.id ?? null,
              }));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rodzaj wydatku"
                helperText="Tag przypisania do bieżącego bookingu (opcjonalnie)."
              />
            )}
          />

          <Autocomplete
            multiple
            options={tags}
            loading={tagsLoading}
            value={selectedTags}
            onChange={(_, value) => {
              setFormState((previousState) => ({
                ...previousState,
                tagIds: value.map((tag) => tag.id),
              }));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tagi dokumentu (opcjonalnie)"
                helperText="Tagi globalne dokumentu kosztowego."
              />
            )}
          />

          <TextField
            label="Opis"
            value={formState.description}
            onChange={(event) => {
              setFormState((previousState) => ({
                ...previousState,
                description: event.target.value,
              }));
              setFormErrors((previousErrors) => ({ ...previousErrors, description: undefined }));
            }}
            multiline
            minRows={4}
            required
            error={Boolean(formErrors.description)}
            helperText={formErrors.description}
          />

          <Typography variant="body2" color="text.secondary">
            Po zapisie przypisanie działa w modelu mapping-only: usunięcie przypisania usuwa wyłącznie
            powiązanie z bookingiem, bez kasowania dokumentu.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Anuluj
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          {isSubmitting ? 'Zapisywanie...' : 'Utwórz i przypisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingSimplifiedCostModal;
